import React, { useState } from 'react';
import { Tabs, Spin } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceDot } from 'recharts';

const { TabPane } = Tabs;

const ElevationChart = ({ elevationData, loadingElevation, elevationStatus, onPointSelect }) => {
  const [activeTab, setActiveTab] = useState('elevation');
  const [activePoint, setActivePoint] = useState(null);
  
  console.log('渲染海拔图表，当前数据:', { 
    dataLength: elevationData?.length || 0, 
    loading: loadingElevation, 
    status: elevationStatus,
    sample: elevationData?.slice(0, 3) || [] 
  });
  
  if (!elevationData || elevationData.length === 0) {
    return (
      <div className="empty-chart" style={{ height: '250px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <p>{loadingElevation ? elevationStatus : '请先规划路线以查看海拔曲线'}</p>
        {loadingElevation && <Spin />}
      </div>
    );
  }
  
  // 处理鼠标在图表上的移动
  const handleMouseMove = (data) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const point = data.activePayload[0].payload;
      setActivePoint(point);
      
      // 检查点数据是否包含有效的经纬度
      if (!point.lng || !point.lat) {
        console.error('点数据中缺少经纬度信息:', point);
        return;
      }
      
      // 通知父组件当前选中的点
      if (onPointSelect && point) {
        console.log('ElevationChart - 选中点:', {
          lng: point.lng,
          lat: point.lat,
          elevation: point.elevation,
          distance: point.distance
        });
        
        // 确保经纬度为数字类型
        const lngNum = typeof point.lng === 'string' ? parseFloat(point.lng) : point.lng;
        const latNum = typeof point.lat === 'string' ? parseFloat(point.lat) : point.lat;
        
        if (isNaN(lngNum) || isNaN(latNum)) {
          console.error('无效的经纬度值:', { lng: point.lng, lat: point.lat });
          return;
        }
        
        onPointSelect({
          lng: lngNum,
          lat: latNum,
          elevation: point.elevation,
          distance: point.distance
        });
      }
    }
  };
  
  // 处理鼠标离开图表
  const handleMouseLeave = () => {
    setActivePoint(null);
    
    // 通知父组件取消选中
    if (onPointSelect) {
      onPointSelect(null);
    }
  };
  
  // 选择要显示的图表内容
  const renderChart = () => {
    console.log('渲染图表，数据:', elevationData.slice(0, 5));
    
    // 图表边距
    const chartMargin = { top: 20, right: 30, left: 20, bottom: 20 };
    
    // 距离格式化函数
    const distanceFormatter = (value) => `${(value/1000).toFixed(1)}km`;
    
    // 自定义活动点样式
    const customActiveDot = (props) => {
      console.log('自定义点属性:', props);
      const { cx, cy, stroke, r } = props;
      return (
        <g>
          {/* 外圈 */}
          <circle cx={cx} cy={cy} r={r + 4} fill="rgba(255, 255, 255, 0.6)" stroke={stroke} strokeWidth={2} />
          {/* 内圈 */}
          <circle cx={cx} cy={cy} r={r} fill={stroke} stroke="none" />
          {/* 垂直参考线 */}
          <line x1={cx} y1={cy} x2={cx} y2={props.chartHeight} stroke={stroke} strokeWidth={1} strokeDasharray="3 3" />
        </g>
      );
    };
    
    if (activeTab === 'elevation') {
      // 海拔曲线
      return (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart 
            data={elevationData} 
            margin={chartMargin}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="distance" 
              label={{ value: '距离 (米)', position: 'insideBottomRight', offset: -10 }} 
              tickFormatter={distanceFormatter}
            />
            <YAxis 
              label={{ value: '海拔 (米)', angle: -90, position: 'insideLeft' }} 
              domain={['dataMin - 10', 'dataMax + 10']}
              tickFormatter={(value) => `${Math.round(value)}m`}
            />
            <RechartsTooltip 
              formatter={(value) => [`${Math.round(value)}米`, '海拔']}
              labelFormatter={distanceFormatter}
              cursor={{ stroke: '#8884d8', strokeWidth: 1, strokeDasharray: '3 3' }}
            />
            <Line 
              type="monotone" 
              dataKey="elevation" 
              stroke="#8884d8" 
              strokeWidth={2}
              dot={false} 
              activeDot={customActiveDot}
              name="海拔"
            />
            
            {/* 当前位置标记 */}
            {activePoint && (
              <ReferenceDot
                x={activePoint.distance}
                y={activePoint.elevation}
                r={6}
                fill="#8884d8"
                stroke="white"
                strokeWidth={2}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      );
    } else if (activeTab === 'climb') {
      // 累计爬升曲线
      return (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart 
            data={elevationData} 
            margin={chartMargin}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="distance" 
              label={{ value: '距离 (米)', position: 'insideBottomRight', offset: -10 }} 
              tickFormatter={distanceFormatter}
            />
            <YAxis 
              label={{ value: '累计爬升 (米)', angle: -90, position: 'insideLeft' }} 
              domain={[0, 'dataMax + 10']}
              tickFormatter={(value) => `${Math.round(value)}m`}
            />
            <RechartsTooltip 
              formatter={(value) => [`${Math.round(value)}米`, '累计爬升']}
              labelFormatter={distanceFormatter}
              cursor={{ stroke: '#4caf50', strokeWidth: 1, strokeDasharray: '3 3' }}
            />
            <Line 
              type="monotone" 
              dataKey="cumulativeElevationGain" 
              stroke="#4caf50" 
              strokeWidth={2}
              dot={false} 
              activeDot={customActiveDot}
              name="累计爬升"
            />
            
            {/* 当前位置标记 */}
            {activePoint && (
              <ReferenceDot
                x={activePoint.distance}
                y={activePoint.cumulativeElevationGain}
                r={6}
                fill="#4caf50"
                stroke="white"
                strokeWidth={2}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      );
    } else if (activeTab === 'gradient') {
      // 坡度曲线
      return (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart 
            data={elevationData} 
            margin={chartMargin}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="distance" 
              label={{ value: '距离 (米)', position: 'insideBottomRight', offset: -10 }} 
              tickFormatter={distanceFormatter}
            />
            <YAxis 
              label={{ value: '坡度 (%)', angle: -90, position: 'insideLeft' }} 
              domain={[
                (dataMin) => Math.min(-2, Math.floor(dataMin)),
                (dataMax) => Math.max(2, Math.ceil(dataMax))
              ]}
              tickFormatter={(value) => `${value}%`}
            />
            <RechartsTooltip 
              formatter={(value) => [`${value}%`, '坡度']}
              labelFormatter={distanceFormatter}
              cursor={{ stroke: '#ff9800', strokeWidth: 1, strokeDasharray: '3 3' }}
            />
            <Line 
              type="monotone" 
              dataKey="gradient" 
              stroke="#ff9800" 
              strokeWidth={2}
              dot={false} 
              activeDot={customActiveDot}
              name="坡度"
            />
            
            {/* 当前位置标记 */}
            {activePoint && (
              <ReferenceDot
                x={activePoint.distance}
                y={activePoint.gradient}
                r={6}
                fill="#ff9800"
                stroke="white"
                strokeWidth={2}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      );
    } else {
      return null;
    }
  };
  
  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab} size="small">
        <TabPane tab="海拔" key="elevation" />
        <TabPane tab="累计爬升" key="climb" />
        <TabPane tab="坡度" key="gradient" />
      </Tabs>
      {renderChart()}
    </div>
  );
};

export default ElevationChart; 